import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import TableContainer from 'components/common/TableContainer';
import httpInjectorService from 'services/http-injector.service';
import nubaxLogo from '../../../../../assets/img/nubaxlogo.png';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import numberToWords from 'number-to-words';
import {
  Button,
  Checkbox,
  HStack,
  VStack,
  Select as ChakraSelect,
  Badge,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Divider,
  Box,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { BulletList } from 'react-content-loader';
import { Empty } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { demoFinalSettlementData } from 'components/demodata/Dummydata';

// FIX 3: Enable dayjs plugins for proper UTC → local time conversion
dayjs.extend(utc);
dayjs.extend(timezone);

const FinalSettlement = () => {
  const [finalSettlementData, setFinalSettlementData] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [payslipLoader, setPayslipLoader] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const [selectedPayslipUser, setSelectedPayslipUser] = useState(null);
  const [isPayslipEnabled, setIsPayslipEnabled] = useState(false);
  const [isPayoutView, setIsPayoutView] = useState(false);
  const [hasRunPayout, setHasRunPayout] = useState(false);
  const [companyHeader, setCompanyHeader] = useState(null);
  const [pdfDownloadLoader, setPdfDownloadLoader] = useState(false);
  const payslipRef = useRef(null);

  const {
    isOpen: isPayslipOpen,
    onOpen: onPayslipOpen,
    onClose: onPayslipClose,
  } = useDisclosure();

  const months = [
    { label: 'January', value: 'January' },
    { label: 'February', value: 'February' },
    { label: 'March', value: 'March' },
    { label: 'April', value: 'April' },
    { label: 'May', value: 'May' },
    { label: 'June', value: 'June' },
    { label: 'July', value: 'July' },
    { label: 'August', value: 'August' },
    { label: 'September', value: 'September' },
    { label: 'October', value: 'October' },
    { label: 'November', value: 'November' },
    { label: 'December', value: 'December' },
  ];

  const years = Array.from({ length: 10 }, (_, i) =>
    (dayjs().year() - 5 + i).toString(),
  );

  const allSelected = false;

  // Reset selections whenever new data loads
  useEffect(() => {
    setSelectedUsers([]);
  }, [finalSettlementData]);

  const toggleAllSelection = useCallback(() => {
    setSelectedUsers([]);
  }, []);

  const toggleRowSelection = useCallback((id) => {
    setSelectedUsers((prev) => (prev.includes(id) ? [] : [id]));
  }, []);

  const handleOpenPayslip = useCallback(
    (rowData) => {
      setSelectedPayslipUser(rowData);
      onPayslipOpen();
    },
    [onPayslipOpen],
  );

  // FIX 3: Helper to safely convert a UTC date string to local time
  const formatDateToLocal = (dateStr) => {
    if (!dateStr) return '-';
    const parsed = dayjs.utc(dateStr).local();
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : '-';
  };

  const formatLeaveBalance = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return '-';
    return num.toFixed(2);
  };

  const displayValue = (value, fallback = '—') =>
    value !== null && value !== undefined && value !== ''
      ? value
      : fallback;

  const formatCurrency = (value) =>
    value !== null && value !== undefined && !Number.isNaN(Number(value))
      ? `₹${Number(value).toFixed(2)}`
      : '₹0.00';

  const borderedBoxStyle = {
    display: 'flex',
    border: '1px solid #ccc',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '20px',
  };

  const borderedSectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid #ccc',
    borderRadius: '8px',
    overflow: 'hidden',
    marginBottom: '20px',
  };

  const columnStyle = { flex: 1, padding: '16px' };
  const verticalDividerStyle = { width: '1px', backgroundColor: '#ccc' };
  const horizontalDividerStyle = {
    height: '1px',
    backgroundColor: '#ccc',
    margin: '0 16px',
  };

  const infoRowStyle = {
    display: 'flex',
    marginBottom: '8px',
    fontSize: '14px',
  };

  const downloadPayslipPDF = async () => {
    if (!payslipRef.current) {
      toast.error('Payslip preview is not ready.');
      return;
    }

    setPdfDownloadLoader(true);
    try {
      const tables = payslipRef.current.querySelectorAll('table');
      tables.forEach((table) => {
        table.style.border = '1px solid black';
        table.style.borderCollapse = 'collapse';
        table.querySelectorAll('th, td').forEach((cell) => {
          cell.style.border = '1px solid black';
          cell.style.color = 'black';
        });
      });

      await new Promise((resolve) => setTimeout(resolve, 500));

      const canvas = await html2canvas(payslipRef.current, {
        scale: 3,
        backgroundColor: '#fff',
        useCORS: true,
      });

      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      pdf.save(
        `Final_Settlement_${selectedPayslipUser?.Employee_ID || 'payslip'}.pdf`,
      );
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to download payslip PDF.');
    } finally {
      setPdfDownloadLoader(false);
    }
  };

  const loadCompanyHeader = useCallback(async () => {
    try {
      const response = await httpInjectorService.getcompanyoverview();
      if (response?.data?.[0]) {
        setCompanyHeader(response.data[0]);
      }
    } catch {
      setCompanyHeader(null);
    }
  }, []);

  useEffect(() => {
    if (isPayslipOpen) {
      loadCompanyHeader();
    }
  }, [isPayslipOpen, loadCompanyHeader]);

  // Shared: only convert UTC dates to local
  const normaliseDates = (item) => ({
    ...item,
    Resignation_Date: formatDateToLocal(item.Resignation_Date),
    Last_Working_Day: formatDateToLocal(item.Last_Working_Day),
    Applied_Date: formatDateToLocal(item.Applied_Date ?? item.applied_date),
  });

  // Use backend values as-is; frontend only formats dates.
  const normaliseRecord = (item) => ({
    ...normaliseDates(item),
    settlement_status: item.settlement_status ?? 'Pending',
    Remaining_Leave:
      item.Remaining_Leave != null && item.Remaining_Leave !== ''
        ? formatLeaveBalance(item.Remaining_Leave)
        : item.Remaining_Leave,
  });

  const deduplicateByUserId = (records) => {
    const map = new Map();

    records.forEach((r) => {
      if (
        !map.has(r.user_id) ||
        Number(r.Final_Amount) > Number(map.get(r.user_id).Final_Amount)
      ) {
        map.set(r.user_id, r);
      }
    });

    return Array.from(map.values());
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <Checkbox
            colorScheme="purple"
            isChecked={allSelected}
            isIndeterminate={selectedUsers.length > 0 && !allSelected}
            onChange={toggleAllSelection}
          />
        ),
        accessor: 'select',
        disableFilters: true,
        Cell: ({ row }) => (
          <Checkbox
            colorScheme="purple"
            isChecked={selectedUsers.includes(row.original.user_id)}
            onChange={() => toggleRowSelection(row.original.user_id)}
          />
        ),
      },
      {
        Header: 'Employee Name',
        accessor: 'Full_Name',
        disableFilters: true,
      },
      {
        Header: 'Resignation Date',
        accessor: 'Resignation_Date',
        disableFilters: true,
      },
      {
        Header: 'Last Working Day',
        accessor: 'Last_Working_Day',
        disableFilters: true,
      },
      {
        Header: 'Month CTC',
        accessor: 'Month_CTC',
        disableFilters: true,
      },
      {
        Header: 'Leave Balance',
        accessor: 'Remaining_Leave',
        disableFilters: true,
        Cell: ({ value }) => formatLeaveBalance(value),
      },
      {
        Header: 'EL Encashment',
        accessor: 'EL_Encashment',
        disableFilters: true,
      },
      {
        Header: 'LOP Deduction',
        accessor: 'LOP_Deduction_amount',
        disableFilters: true,
      },
      {
        Header: 'Final Payable Amount',
        accessor: 'Final_Amount',
        disableFilters: true,
        Cell: ({ row }) => {
          if (!hasRunPayout) {
            return '0.00'; // ✅ always show 0 initially
          }

          return row.original.Final_Amount ?? '0.00';
        },
      },
      {
        Header: 'Status',
        accessor: 'settlement_status',
        disableFilters: true,
        Cell: ({ row }) => {
          const [isEditing, setIsEditing] = useState(false);

          const currentStatus = row.original.settlement_status || 'Pending';

          const statusOptions = [
            { label: 'Pending', value: 'Pending', color: 'orange' },
            { label: 'Salary on Hold', value: 'Salary on Hold', color: 'red' },
            { label: 'Paid', value: 'Paid', color: 'green' },
          ];

          const colorMap = {
            Pending: 'orange',
            'Salary on Hold': 'red',
            Paid: 'green',
          };

          const handleStatusChange = async (value) => {
            try {
              const payload = {
                user_id: row.original.user_id,
                status: value,
              };

              const res =
                await httpInjectorService.updateFinalSettlementPayoutDetails(
                  payload,
                );

              if (res?.status === 'success') {
                toast.success('Status updated');

                // update UI instantly
                setFinalSettlementData((prev) =>
                  prev.map((item) =>
                    item.user_id === row.original.user_id
                      ? { ...item, settlement_status: value }
                      : item,
                  ),
                );

                setIsEditing(false);
              } else {
                toast.error(res?.message || 'Failed to update status');
              }
            } catch (err) {
              toast.error(err?.message || 'Error updating status');
            }
          };

          if (isEditing) {
            return (
              <ChakraSelect
                size="xs"
                autoFocus
                value={currentStatus}
                onBlur={() => setIsEditing(false)}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </ChakraSelect>
            );
          }

          return (
            <Badge
              colorScheme={colorMap[currentStatus] || 'gray'}
              cursor="pointer"
              onClick={() => setIsEditing(true)}
              px={2}
              py={1}
              borderRadius="md"
            >
              {currentStatus}
            </Badge>
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        disableFilters: true,
        Cell: ({ row }) => {
          const isCompleted = row.original.status === 'Completed';
          return (
            <Button
              size="xs"
              colorScheme="purple"
              variant="outline"
              isDisabled={isCompleted}
              onClick={() => handleOpenPayslip(row.original)}
            >
              {isCompleted ? 'Payslip Done' : 'Generate Payslip'}
            </Button>
          );
        },
      },
    ],
    [
      selectedUsers,
      allSelected,
      toggleAllSelection,
      toggleRowSelection,
      handleOpenPayslip,
    ],
  );

  const isFiltersValid = selectedMonth !== null && selectedYear !== null;

  useEffect(() => {
    setIsPayslipEnabled(false);
    setFinalSettlementData([]);
    setSelectedUsers([]);
    setHasRunPayout(false); // ⭐ ADD THIS
  }, [selectedMonth, selectedYear]);

  const getFinalSettlementDetails = async (isPayout = false , showToast = true) => {
    const payoutMode = isPayout === true;
     const isDemoMode = localStorage.getItem("demoMode") === "true";

    if (!isDemoMode && !isFiltersValid) {
  toast.error('Please select both month and year.');
  return;
}

    setSpinner(true);
    setIsLoading(true);

    const reqBody = {
  month: selectedMonth?.label || 'June',
  year: Number(selectedYear?.value || '2026'),
};

    try {
     const isDemoMode = localStorage.getItem("demoMode") === "true";

  const response = isDemoMode
  ? demoFinalSettlementData
  : await httpInjectorService.getFinalSettlementDetails(reqBody);

      if (response?.status === 'success') {
        const normalised = deduplicateByUserId(
          (response.data || []).map(normaliseRecord),
        );

        // ✅ show only PAID users in payout tab
        const filtered = payoutMode
          ? normalised.filter((item) => item.settlement_status === 'Paid')
          : normalised;

        setFinalSettlementData(filtered);
        if (showToast) {
          toast.success(response.message, { autoClose: 1000 });
        }
      } else {
        setFinalSettlementData([]);
        toast.error(response?.message || 'No data found');
      }
    } catch (err) {
      setFinalSettlementData([]);
      toast.error(err?.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
      setSpinner(false);
    }
  };

  useEffect(() => {
  const isDemoMode = localStorage.getItem("demoMode") === "true";

  if (isDemoMode) {
    getFinalSettlementDetails(false, false);
  }
}, []);

  const runFinalSettlement = async () => {
    if (buttonLoader) return;

    if (selectedUsers.length !== 1) {
      toast.error('Please select one employee.');
      return;
    }

    if (
      !finalSettlementData.some((item) => item.user_id === selectedUsers[0])
    ) {
      toast.error('Please select at least one employee.');
      return;
    }

    if (!isFiltersValid) {
      toast.error('Please select both month and year.');
      return;
    }

    // Disable payslip generation while payout is in progress.
    setIsPayslipEnabled(false);
    setButtonLoader(true);

    const uniqueUsers = [selectedUsers[0]];

    const reqBody = {
      users: uniqueUsers,
      month: selectedMonth.label,
      year: selectedYear.value,
      monthName: selectedMonth.label,
    };

    try {
      const response = await httpInjectorService.runFinalSettlement(reqBody);

      if (response?.status === 'success') {
        toast.success(response.message);

        setHasRunPayout(true); // ⭐ ADD THIS LINE

        setSelectedUsers([]);
        setIsPayslipEnabled(true);
        await getFinalSettlementDetails();
      } else {
        toast.error(response?.message || 'Failed to run payroll.');
      }
    } catch (err) {
      toast.error(err?.message || 'An unexpected error occurred.');
    } finally {
      setButtonLoader(false);
    }
  };

  const generatePayslip = async () => {
    if (!selectedPayslipUser) return;

    setPayslipLoader(true);

    const payload = {
      users: selectedPayslipUser.user_id,
      Month: selectedMonth?.label,
      Year: selectedYear?.value,
    };

    try {
      const response =
        await httpInjectorService.generateFinalSettlementPayslip(payload);

      if (response?.status === 'success') {
        toast.success(response.message || 'Payslip generated successfully.');
        onPayslipClose();
        await getFinalSettlementDetails();
      } else {
        toast.error(response?.message || 'Failed to generate payslip.');
      }
    } catch (err) {
      toast.error(err?.message || 'An unexpected error occurred.');
    } finally {
      setPayslipLoader(false);
    }
  };

  const hasData = finalSettlementData.length > 0;

  const latestUser = finalSettlementData.find(
    (item) => item.user_id === selectedPayslipUser?.user_id,
  );

  return (
    <>
      <div className="mt-3">
        <Box float="right">
          <HStack spacing={3}>
            <Button
              onClick={runFinalSettlement}
              rounded="3"
              colorScheme="purple"
              isLoading={buttonLoader}
              isDisabled={buttonLoader || selectedUsers.length !== 1}
            >
              Run PayOut
            </Button>

            <Button
              rounded="3"
              colorScheme="purple"
              variant={isPayoutView ? 'solid' : 'outline'}
              onClick={() => {
                setIsPayoutView(true);
                setHasRunPayout(true); // ensures values show in payout view
                getFinalSettlementDetails(true);
              }}
            >
              Payout
            </Button>

            {isPayoutView && (
              <Button
                rounded="3"
                colorScheme="purple"
                onClick={() => {
                  setIsPayoutView(false);
                  setHasRunPayout(false); // reset payout state
                  setSelectedUsers([]);
                  getFinalSettlementDetails(false, false); // ✅ no toast on back
                }}
              >
                Back
              </Button>
            )}
          </HStack>
        </Box>
        <Box clear="both" />
        {/* Filters Row */}
        <VStack align="flex-start" spacing={4} mt={4}>
          <HStack spacing={4}>
            <ChakraSelect
              placeholder="Select Month"
              value={selectedMonth?.value || ''}
              onChange={(e) => {
                const found = months.find((m) => m.value === e.target.value);
                setSelectedMonth(found || null);
              }}
              size="sm"
              borderColor="purple.300"
              focusBorderColor="purple.500"
              rounded="md"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </ChakraSelect>

            <ChakraSelect
              placeholder="Select Year"
              value={selectedYear?.value || ''}
              onChange={(e) => {
                setSelectedYear(
                  e.target.value
                    ? { value: e.target.value, label: e.target.value }
                    : null,
                );
              }}
              size="sm"
              borderColor="purple.300"
              focusBorderColor="purple.500"
              rounded="md"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </ChakraSelect>

            <div className="col-sm-2">
              <Button
                onClick={() => getFinalSettlementDetails(false)}
                colorScheme="purple"
                size="sm"
                isLoading={spinner}
                isDisabled={!isFiltersValid}
                rounded="md"
              >
                Submit
              </Button>
            </div>
          </HStack>
        </VStack>

        {/* Table */}
        <div className="mt-3">
          {isLoading ? (
            <BulletList />
          ) : !hasData ? (
            <Empty className="mt-4" />
          ) : (
            <>
              <TableContainer
                columns={columns}
                data={finalSettlementData}
                isGlobalFilter={true}
                customPageSize={10}
                className="custom-header-css"
              />
            </>
          )}
        </div>
      </div>

      {/* Payslip Verification Modal */}
      <Modal
        isOpen={isPayslipOpen}
        onClose={onPayslipClose}
        size="xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="720px">
          <ModalHeader>Verify & Generate Payslip</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPayslipUser && (
              <>
                <Box
                  ref={payslipRef}
                  className="bg-white rounded-xl p-4"
                  color="black"
                >
                  <Box display="flex" justifyContent="center" mb={5}>
                    <img
                      src={companyHeader?.logo_url || nubaxLogo}
                      height="100"
                      width="100"
                      alt="Company Logo"
                      style={{
                        display: 'block',
                        margin: '0 auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>
                  <h2 className="text-center fw-bold mt-2">
                    {displayValue(companyHeader?.company_name)}
                  </h2>
                  <p className="text-center mb-0">
                    {displayValue(
                      companyHeader?.registered_office ||
                        companyHeader?.corporate_office,
                    )}
                  </p>
                  <h4 className="text-center mt-3">
                    Final Settlement Payslip for the Month of{' '}
                    {displayValue(selectedMonth?.label)},{' '}
                    {displayValue(selectedYear?.value)}
                  </h4>

                  <Divider my={4} />

                  {/* Employee info — bordered two-column box */}
                  <div style={borderedBoxStyle}>
                    <div style={columnStyle}>
                      {[
                        ['Name', selectedPayslipUser.Full_Name],
                        ['Designation', selectedPayslipUser.Designation_Name],
                        ['Department', selectedPayslipUser.Department_Name],
                        [
                          'Last Working Day',
                          selectedPayslipUser.Last_Working_Day,
                        ],
                        [
                          'Leave Balance',
                          formatLeaveBalance(
                            selectedPayslipUser.Remaining_Leave,
                          ),
                        ],
                        ['LOP', selectedPayslipUser.LOP_Leaves ?? 0],
                      ].map(([label, value]) => (
                        <div style={infoRowStyle} key={label}>
                          <span style={{ width: '42%', fontWeight: 500 }}>
                            {label}:
                          </span>
                          <span style={{ width: '58%' }}>
                            {displayValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div style={verticalDividerStyle} />
                    <div style={columnStyle}>
                      {[
                        ['Employee ID', selectedPayslipUser.Employee_ID],
                        [
                          'Sub Department',
                          selectedPayslipUser.SubDepartment_Name,
                        ],
                        [
                          'Resignation Date',
                          selectedPayslipUser.Resignation_Date,
                        ],
                        [
                          'Comp Off Balance',
                          selectedPayslipUser.Comp_Off_Balance ?? 0,
                        ],
                      ].map(([label, value]) => (
                        <div style={infoRowStyle} key={label}>
                          <span style={{ width: '42%', fontWeight: 500 }}>
                            {label}:
                          </span>
                          <span style={{ width: '58%' }}>
                            {displayValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Divider my={4} />

                  {/* Earnings & deductions — bordered box */}
                  <div style={borderedSectionStyle}>
                    <div style={{ display: 'flex' }}>
                      <div style={columnStyle}>
                        <h6 className="mb-2" style={{ fontWeight: 'bold' }}>
                          Earnings
                        </h6>
                        <Divider mb={2} />
                        {[
                          // ['Monthly Salary', selectedPayslipUser.Monthly_Salary],
                          [
                            'EL Encashment',
                            selectedPayslipUser.EL_Encashment,
                          ],
                          [
                            'Basic',
                            selectedPayslipUser.Month_Basic,
                          ],
                          [
                            'HRA',
                            selectedPayslipUser.Month_HRA,
                          ],
                          [
                            'Conveyance Allowance',
                            selectedPayslipUser.Conveyance_Allowance,
                          ],
                          [
                            'Special Allowance',
                            selectedPayslipUser.special_allowance_month || 0.00,
                          ],
                          [
                            'Variable',
                            selectedPayslipUser.variable || 0.00,
                          ],
                        ].map(([label, value]) => (
                          <div style={infoRowStyle} key={label}>
                            <span style={{ width: '50%' }}>{label}</span>
                            <span style={{ width: '50%' , marginLeft: '28px'}}>
                              {formatCurrency(value)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div style={verticalDividerStyle} />
                      <div style={columnStyle}>
                        <h6 className="mb-2" style={{ fontWeight: 'bold' }}>
                          Deductions
                        </h6>
                        <Divider mb={2} />
                        <div style={infoRowStyle}>
                          <span style={{ width: '50%' }}>Loss of Pay</span>
                          <span style={{ width: '50%' }}>
                            {formatCurrency(
                              selectedPayslipUser.LOP_Deduction_amount,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={horizontalDividerStyle} />
                    <div style={{ padding: '16px' }}>
                      <div style={{ display: 'flex' }}>
                        <div style={{ ...columnStyle, padding: 0 }}>
                          <div style={infoRowStyle}>
                            <span
                              style={{ width: '50%', fontWeight: 'bold' }}
                            >
                              Total Earnings (₹)
                            </span>
                            <span style={{ width: '50%' }}>
                              {formatCurrency(
                                Number(selectedPayslipUser.Monthly_Salary || 0) +
                                  Number(
                                    selectedPayslipUser.EL_Encashment || 0,
                                  ) +
                                  Number(
                                    selectedPayslipUser.Conveyance_Allowance ||
                                      0,
                                  ),
                              )}
                            </span>
                          </div>
                        </div>
                        <div style={{ ...columnStyle, padding: 0 }}>
                          <div style={infoRowStyle}>
                            <span
                              style={{ width: '50%', fontWeight: 'bold' }}
                            >
                              Total Deductions (₹)
                            </span>
                            <span style={{ width: '50%' }}>
                              {formatCurrency(
                                selectedPayslipUser.LOP_Deduction_amount,
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Net pay */}
                  <div style={{ marginBottom: '20px' }}>
                    <div style={infoRowStyle}>
                      <span style={{ width: '30%', fontWeight: 'bold' }}>
                        Net Pay For The Month
                      </span>
                      <span style={{ width: '70%' }}>
                        {formatCurrency(
                          !hasRunPayout
                            ? 0
                            : latestUser?.Final_Amount ??
                                selectedPayslipUser.Final_Amount,
                        )}
                      </span>
                    </div>
                    <div style={{ ...infoRowStyle, marginTop: '8px' }}>
                      <span>
                        Rupees{' '}
                        {(() => {
                          const netPay = Number(
                            !hasRunPayout
                              ? 0
                              : latestUser?.Final_Amount ??
                                  selectedPayslipUser.Final_Amount,
                          );
                          return netPay && !Number.isNaN(netPay)
                            ? numberToWords.toWords(Math.round(netPay))
                            : 'Zero';
                        })()}{' '}
                        Only
                      </span>
                    </div>
                  </div>

                  <Divider />
                  <p className="text-center mb-0" style={{ fontSize: '13px' }}>
                    This is a system-generated payslip and does not require a
                    signature.
                  </p>
                </Box>

                <Box textAlign="center" mt={5}>
                  <Button
                    colorScheme="purple"
                    variant="outline"
                    size="sm"
                    onClick={downloadPayslipPDF}
                    isLoading={pdfDownloadLoader}
                    isDisabled={pdfDownloadLoader}
                  >
                    Download PDF
                  </Button>
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onPayslipClose} size="sm">
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={generatePayslip}
              isLoading={payslipLoader}
              size="sm"
              isDisabled={!isPayslipEnabled || buttonLoader}
            >
              Confirm & Generate Payslip
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default FinalSettlement;
